import DOMPurify from 'dompurify';
import { useMemo, type HTMLAttributes } from 'react';

/**
 * HTML van de server tonen, met een tweede slot in de browser.
 *
 * De berichttekst komt uit de renderer met een witte lijst (`@up2d8/rich-text`, ADR-0037) en kan
 * dus geen `<script>` bevatten. Maar wat er in `body_html` staat, staat er ook zonder die renderer:
 * een import van het oude platform, een migratie, een fout in een latere versie. Daarom gaat de
 * HTML hier nog eens door DOMPurify voor hij in de pagina komt: wat de renderer schrijft komt er
 * ongeschonden door, de rest niet.
 */
export function sanitizeHtml(html: string): string {
  return DOMPurify.sanitize(html, {
    USE_PROFILES: { html: true },
    // De renderer zet `target="_blank"` op links; DOMPurify laat dat standaard vallen.
    ADD_ATTR: ['target'],
  });
}

// Elke link die in een nieuw tabblad opent, verliest zijn greep op dit venster (reverse tabnabbing),
// ook als de `rel` van de server ontbreekt.
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A' && node.getAttribute('target') === '_blank') {
    node.setAttribute('rel', 'noopener noreferrer nofollow');
  }
});

type SafeHtmlProps = Omit<
  HTMLAttributes<HTMLDivElement>,
  'dangerouslySetInnerHTML' | 'children'
> & {
  html: string;
};

/** Een `<div>` met opgeschoonde HTML erin; de enige plek in de web-app met `innerHTML`. */
export function SafeHtml({ html, ...props }: SafeHtmlProps) {
  const clean = useMemo(() => sanitizeHtml(html), [html]);
  return <div {...props} dangerouslySetInnerHTML={{ __html: clean }} />;
}
